import { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import InfiniteScroll from "react-infinite-scroll-component";
import Grid from "@mui/material/Grid";
import { Link } from "react-router-dom";
import BookmarkAddedRoundedIcon from "@mui/icons-material/BookmarkAddedRounded";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import ShareIcon from "@mui/icons-material/Share";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PostCardItem from "../../components/forum/PostCardItem";
import ReplyForm from "../../components/forum/ReplyForm";
import AuthContext from "../../context/AuthContext";
import React from "react";
import {
  PollType,
  UserType,
  ThreadType,
  ReactionType,
} from "../../utils/types";
import moment from "moment";
import ThreadForm from "../../components/forum/ThreadForm";
import { Button } from "@mui/material";
import ThreadEditForm from "../../components/forum/ThreadEditForm";
import { ArrowBack, Attachment, BurstModeTwoTone } from "@mui/icons-material";
import "react-quill-new/dist/quill.snow.css";

const Thread = () => {
  const navigate = useNavigate();
  let { user } = useContext(AuthContext);
  const currentUser: UserType = user;

  // extract thread id
  let params = useParams();
  let threadID = params.id;

  // initalize thread and posts component state
  let [thread, setThread] = useState<ThreadType>(null);
  let [posts, setPosts] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [pin, setPin] = useState(false);
  const [poll, setPoll] = useState<PollType>(null);
  const [users, setUsers] = useState<UserType[]>([]);
  const [emojiMenuOpen, setEmojiMenuOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [reactions, setReactions] = useState<ReactionType[]>([]);
  const [files, setFiles] = useState<string[]>([]);

  useEffect(() => {
    const getAllUsers = async () => {
      const response = await fetch(`/api/getAllUsers/`);
      const data = await response.json();
      setUsers(data);
    };
    getAllUsers();
  }, []);

  // update bookmark icon for the thread
  useEffect(() => {
    let getBookmark = async () => {
      if (currentUser !== null) {
        let response = await fetch(`/api/pin/${threadID}&&${currentUser.id}`);
        let data = await response.json();
        setPin(JSON.parse(data.pinned));
      }
    };
    getBookmark();
  }, [threadID]);

  // trigger thread update
  useEffect(() => {
    let getThread = async () => {
      let response = await fetch(`/api/threads/${threadID}`);
      let data = await response.json();
      setThread(data);
    };
    const fetchPoll = async () => {
      const response = await fetch(`/api/threads/${threadID}/poll/`);
      if (response.ok) {
        const data = await response.json();
        setPoll(data);
      }
    };
    const fetchReactions = async () => {
      const response = await fetch(`/api/threads/${threadID}/react/`);
      const data = await response.json();
      setReactions(data);
    };

    getThread();
    fetchPoll();
    fetchReactions();
  }, [threadID]);

  // trigger posts update
  useEffect(() => {
    let getPosts = async () => {
      // fetch the posts from api endpoint
      let response = await fetch(`/api/threads/${threadID}/posts?page=${page}`);

      // parse the data in json
      let data = await response.json();

      // update the state of threads
      setPosts(data.results);

      // check if there is more posts
      if (data.next === null) {
        setHasMore(false);
      }
      setPage(page + 1);
    };
    getPosts();
  }, [threadID]);

  useEffect(() => {
    const fetchFiles = async () => {
      const response = await fetch(`/api/threads/${threadID}/files`);
      const data = await response.json();
      setFiles(data?.files);
    };
    fetchFiles();
  }, [threadID]);

  // fetch next posts
  const getMorePosts = async () => {
    try {
      // fetch the posts from api endpoint
      const response = await fetch(
        `/api/threads/${threadID}/posts?page=${page}`
      );

      // parse the data in json
      let data = await response.json();

      return data.results;
    } catch (err) {}
  };

  const fetchData = async () => {
    // get more posts from next fetch
    let morePosts = await getMorePosts();

    // update the thread state by combining data
    setPosts([...posts, ...morePosts]);

    // check the fetch of last page, if yes, HasMore is false
    if (morePosts.length === 0 || morePosts.length < 10) {
      setHasMore(false);
    }
    setPage(page + 1);
  };

  // handle bookmark button
  const handleBookmark = async () => {
    // update bookmark
    setPin(!pin);

    const response = await fetch(`/api/pin/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        user: currentUser.id,
        thread: threadID,
        pin: pin ? false : true,
      }),
    });
    const data = await response.json();
  };

  const handleVote = async (optionId: string) => {
    if (!currentUser) return;

    try {
      const response = await fetch(`/api/threads/${threadID}/vote/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user: currentUser.id,
          option: optionId,
        }),
      });

      if (response.ok) {
        const updatedPoll = await response.json();
        setPoll(updatedPoll["poll"]);
      } else {
        console.error("Failed to vote");
      }
    } catch (error) {
      console.error("Error voting:", error);
    }
  };

  const handleEmojiClick = async (emoji: string) => {
    if (!currentUser) return;

    try {
      const response = await fetch(`/api/threads/${threadID}/react/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user: currentUser.id,
          thread: threadID,
          emoji: emoji,
        }),
      });

      if (response.ok) {
        setEmojiMenuOpen(false);
        setReactions(await response.json());
      } else {
        console.error("Failed to add emoji reaction");
      }
    } catch (error) {
      console.error("Error adding emoji reaction:", error);
    }
  };

  const downloadFile = async (link: RequestInfo, fileName: string) => {
    try {
      const response = await fetch(link);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const anchorElement = document.createElement("a");
      anchorElement.href = url;
      anchorElement.setAttribute("download", fileName);
      anchorElement.target = "_blank";
      document.body.appendChild(anchorElement);

      anchorElement.click();
      anchorElement?.parentNode?.removeChild(anchorElement);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div style={{ marginTop: 100 }}>
      <Container>
        <Card sx={{ minWidth: 300, marginTop: 3 }} elevation={3}>
          <CardContent>
            <Grid container>
              <Grid item xs={12}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{ m: 1, p: 1, flexGrow: 1 }}
                    variant="h6"
                    component="div"
                  >
                    {thread?.subject}
                  </Typography>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/forum")}
                      startIcon={<ArrowBack />}
                      style={{
                        maxWidth: "180px",
                        maxHeight: "32px",
                        minWidth: "40px",
                        minHeight: "32px",
                      }}
                    >
                      {window.innerWidth > 700 ? "Zurück" : ""}
                    </Button>
                    {(currentUser?.id?.toString().trim() ===
                      thread?.creator_id?.toString().trim() ||
                      currentUser?.bemerkung === "GF") && (
                      <IconButton
                        aria-label="settings"
                        onClick={() => setSettingsMenuOpen(!settingsMenuOpen)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    )}
                    {settingsMenuOpen && (
                      <div
                        style={{
                          position: "absolute",
                          zIndex: 1,
                          background: "white",
                          border: "1px solid #ccc",
                          borderRadius: "5px",
                          padding: "10px",
                        }}
                      >
                        <ThreadEditForm initialThread={thread} />
                        <Button
                          variant="outlined"
                          onClick={async () => {
                            try {
                              const response = await fetch(
                                `/api/threads/${threadID}`,
                                {
                                  method: "DELETE",
                                  headers: {
                                    "Content-Type": "application/json",
                                  },
                                }
                              );

                              if (response.ok) {
                                // Redirect to forum main page or show a success message
                                window.location.href = "/forum";
                              } else {
                                console.error("Failed to delete thread");
                              }
                            } catch (error) {
                              console.error("Error deleting thread:", error);
                            }
                          }}
                          style={{
                            maxWidth: "180px",
                            maxHeight: "32px",
                            minWidth: "100px",
                            minHeight: "32px",
                          }}
                        >
                          Löschen
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className="ql-editor"
                  dangerouslySetInnerHTML={{ __html: thread?.content }}
                  style={{ whiteSpace: "pre-wrap" }}
                />
                <style>
                  {`
                .ql-editor img {
                  max-width: 100%;
                  height: auto;
                }
                `}
                </style>
                {poll && (
                  <div>
                    <Typography
                      sx={{ m: 1, p: 1 }}
                      variant="h6"
                      component="div"
                    >
                      {poll.question}
                      {poll.options?.map((option, index) => (
                        <div
                          key={index}
                          style={{
                            marginBottom: "20px",
                            backgroundColor: "#e0e0e0",
                          }}
                          onClick={() => handleVote(option.id)}
                        >
                          <Typography sx={{ m: 1, p: 1 }} variant="body1">
                            {option.option_text}
                          </Typography>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                            }}
                          >
                            <div
                              style={{
                                width: `${((option.votes ? option.votes.split(",").length : 0) / poll.options.reduce((acc, opt) => acc + (opt.votes ? opt.votes.split(",").length : 0), 0)) * 100}%`,
                                backgroundColor: "#1d9538",
                                height: "20px",
                                borderRadius: "5px",
                                marginRight: "10px",
                                transition: "width 0.3s ease-in-out",
                              }}
                            ></div>
                            <Typography variant="body2">
                              {option.votes
                                ? option.votes.split(",").length
                                : 0}
                              {option.votes.split(",").length === 1 &&
                              option.votes[0]
                                ? " Stimme"
                                : " Stimmen"}
                            </Typography>
                          </div>
                          {!poll.is_anonymous && (
                            <Typography
                              sx={{ m: 1, p: 1 }}
                              variant="body2"
                              color="text.secondary"
                            >
                              {option.votes
                                .split(",")
                                .map((ma) => {
                                  const user = users.find(
                                    (u) =>
                                      u.id?.toString() === ma.trim()?.toString()
                                  );
                                  return user
                                    ? `${user.last_name} ${user.first_name.slice(0, 2)}.`
                                    : "";
                                })
                                .sort((a, b) => a.localeCompare(b))
                                .join(", ")}
                            </Typography>
                          )}
                        </div>
                      ))}
                    </Typography>
                  </div>
                )}
                {files && files.length > 0 && (
                  <Typography>
                    <b>Anhänge:</b>
                  </Typography>
                )}
                {files &&
                  files.length > 0 &&
                  files.map((file) => (
                    <div
                      key={file}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <div>
                        <a
                          href="#"
                          onClick={(e) => {
                            downloadFile(file, file?.split("/").pop());
                          }}
                          style={{
                            textDecoration: "underline",
                            color: "blue",
                            cursor: "pointer",
                          }}
                        >
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            {file?.endsWith(".jpg") ||
                            file?.endsWith(".jpeg") ||
                            file?.endsWith(".png") ||
                            file?.endsWith(".gif") ? (
                              <img
                                src={file}
                                alt="preview"
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  objectFit: "cover",
                                  marginRight: "10px",
                                  borderRadius: "5px",
                                }}
                              />
                            ) : (
                              <Attachment
                                style={{
                                  marginRight: "10px",
                                  color: "#757575",
                                  fontSize: "50px",
                                }}
                              />
                            )}
                            <Typography variant="body1">
                              {file?.split("/").pop() ?? "Unbekannte Datei"}
                            </Typography>
                          </div>
                        </a>
                      </div>
                    </div>
                  ))}
                <div>
                  {reactions?.length > 0 &&
                    Object.entries(
                      reactions.reduce(
                        (acc, reaction) => {
                          acc[reaction.emoji] = (acc[reaction.emoji] || 0) + 1;
                          return acc;
                        },
                        {} as { [key: string]: number }
                      )
                    ).map(([emoji, count], index) => (
                      <span key={index} style={{ marginRight: 10 }}>
                        {emoji}({count})
                      </span>
                    ))}
                </div>
              </Grid>
            </Grid>
          </CardContent>

          <CardActions sx={{ marginBottom: 2, marginRight: 3 }}>
            <Grid container justifyContent="space-between">
              <Typography sx={{ m: 1, p: 1 }} color="text.secondary">
                <Link
                  to={`/profile/${thread?.creator_id}`}
                  style={{ color: "grey" }}
                >
                  {typeof thread?.creator === "string" ? thread.creator : thread?.creator?.username}
                </Link>{" "}
                am{" "}
                {moment(thread?.updated)
                  .locale("de")
                  .format("DD.MM.YYYY HH:mm")}
              </Typography>

              <div>
                {/* <IconButton aria-label="share">
                <ShareIcon />
              </IconButton> */}
                <IconButton
                  aria-label="react"
                  sx={{ marginRight: 2 }}
                  onClick={() => setEmojiMenuOpen(!emojiMenuOpen)}
                >
                  <span role="img" aria-label="emoji">
                    😊
                  </span>
                </IconButton>
                {emojiMenuOpen && (
                  <div
                    style={{
                      position: "absolute",
                      zIndex: 1,
                      background: "white",
                      border: "1px solid #ccc",
                      borderRadius: "5px",
                      padding: "10px",
                    }}
                  >
                    {["😊", "😂", "😍", "😢", "😡", "👍", "👎"].map(
                      (emoji, index) => (
                        <span
                          key={index}
                          role="img"
                          aria-label="emoji"
                          style={{
                            fontSize: "24px",
                            cursor: "pointer",
                            margin: "5px",
                          }}
                          onClick={() => handleEmojiClick(emoji)}
                        >
                          {emoji}
                        </span>
                      )
                    )}
                  </div>
                )}
                {currentUser ? (
                  <>
                    {pin && (
                      <IconButton
                        aria-label="bookmark"
                        sx={{ marginRight: 2 }}
                        color="secondary"
                        onClick={handleBookmark}
                      >
                        <BookmarkAddedRoundedIcon />
                      </IconButton>
                    )}
                    {!pin && (
                      <IconButton
                        aria-label="bookmark"
                        sx={{ marginRight: 2 }}
                        onClick={handleBookmark}
                      >
                        <BookmarkAddIcon />
                      </IconButton>
                    )}
                  </>
                ) : (
                  <div></div>
                )}
              </div>
            </Grid>
          </CardActions>
        </Card>

        <InfiniteScroll
          dataLength={posts.length} //This is important field to render the next data
          next={fetchData}
          hasMore={hasMore}
          loader={
            <h4 style={{ textAlign: "center", marginTop: 20 }}>Lädt...</h4>
          }
          endMessage={
            <p
              style={{ textAlign: "center", marginTop: 40 }}
              className="text-muted"
            >
              Du hast alle Beiträge gesehen.
            </p>
          }
        >
          {posts.map((post, index) => (
            <PostCardItem key={index} post={post} />
          ))}
        </InfiniteScroll>
      </Container>

      <ReplyForm thread={thread} />
    </div>
  );
};

export default Thread;
